import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

export function Tilt({
  children,
  className,
  style,
  rotationFactor = 15,
  isRevese = false,
  springOptions = { stiffness: 0.15, damping: 0.8 },
}) {
  const elementRef = useRef(null)
  const requestRef = useRef(null)
  const previousTimeRef = useRef(null)

  // Position state stored in refs to avoid re-renders
  const rotationRef = useRef({ x: 0, y: 0 }) // Current rotation values
  const targetRotationRef = useRef({ x: 0, y: 0 }) // Target rotation values
  const velocityRef = useRef({ x: 0, y: 0 }) // Current velocity

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // Reset rotation and velocity
    const resetRotation = () => {
      targetRotationRef.current = { x: 0, y: 0 }
    }

    // Calculate target rotation based on mouse position
    const calculateTargetRotation = (e) => {
      if (!elementRef.current) return

      const rect = elementRef.current.getBoundingClientRect()
      const width = rect.width
      const height = rect.height

      // Get mouse position relative to element
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      // Calculate normalized position (-0.5 to 0.5)
      const normalizedX = (mouseX / width) - 0.5
      const normalizedY = (mouseY / height) - 0.5

      // Set target rotation with proper sign based on isRevese
      targetRotationRef.current = {
        x: isRevese ? -normalizedY * rotationFactor : normalizedY * rotationFactor,
        y: isRevese ? normalizedX * rotationFactor : -normalizedX * rotationFactor,
      }
    }

    // Animation loop using spring physics
    const animateSpring = (time) => {
      if (previousTimeRef.current === undefined) {
        previousTimeRef.current = time
      }

      const deltaTime = Math.min((time - previousTimeRef.current) / 1000, 0.1) // seconds, capped to avoid huge jumps
      previousTimeRef.current = time

      // For each axis (x and y)
      const axes = ['x', 'y']
      let stillMoving = false

      axes.forEach(axis => {
        // Spring physics
        // F = -kx - cv (where k = stiffness, c = damping, x = displacement, v = velocity)
        const displacement = rotationRef.current[axis] - targetRotationRef.current[axis]
        const springForce = -springOptions.stiffness * displacement
        const dampingForce = -springOptions.damping * velocityRef.current[axis]
        const force = springForce + dampingForce

        // Update velocity (a = F/m, where we assume m = 1)
        velocityRef.current[axis] += force * deltaTime

        // Update position
        rotationRef.current[axis] += velocityRef.current[axis] * deltaTime

        // Check if still moving significantly
        if (
          Math.abs(displacement) > 0.01 ||
          Math.abs(velocityRef.current[axis]) > 0.01
        ) {
          stillMoving = true
        }
      })

      // Apply transform to element
      if (elementRef.current) {
        elementRef.current.style.transform = `perspective(1000px) rotateX(${rotationRef.current.x}deg) rotateY(${rotationRef.current.y}deg)`
      }

      // Continue animation if still moving
      if (stillMoving) {
        requestRef.current = requestAnimationFrame(animateSpring)
      } else {
        requestRef.current = null
      }
    }

    // Event handlers
    const handleMouseMove = (e) => {
      calculateTargetRotation(e)

      // Start animation loop if not already running
      if (!requestRef.current) {
        previousTimeRef.current = undefined
        requestRef.current = requestAnimationFrame(animateSpring)
      }
    }

    const handleMouseLeave = () => {
      resetRotation()

      // Ensure animation is running to return to flat state
      if (!requestRef.current) {
        previousTimeRef.current = undefined
        requestRef.current = requestAnimationFrame(animateSpring)
      }
    }

    // Add event listeners
    element.addEventListener('mousemove', handleMouseMove)
    element.addEventListener('mouseleave', handleMouseLeave)

    // Clean up
    return () => {
      element.removeEventListener('mousemove', handleMouseMove)
      element.removeEventListener('mouseleave', handleMouseLeave)

      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current)
      }
    }
  }, [rotationFactor, isRevese, springOptions.stiffness, springOptions.damping])

  return (
    <div
      ref={elementRef}
      className={className}
      style={{
        transformStyle: 'preserve-3d',
        willChange: 'transform',
        transition: 'transform 0s',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

Tilt.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  rotationFactor: PropTypes.number,
  isRevese: PropTypes.bool,
  springOptions: PropTypes.object,
}

export default Tilt
